import { action, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";

class ArtistUploadViewStore extends MrbBaseViewStore {
    @observable phase = "idle";   // "idle" | "uploading" | "pending" | "result"
    @observable artistName = "";
    @observable albumName = "";
    @observable description = "";
    @observable files = [];
    @observable fileProgresses = {};
    @observable result = null;
    @observable error = null;
    @observable pendingSession = null;
    @observable producerId = null;
    @observable isFinalizing = false;
    _initSeq = 0;

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;
    }

    @action.bound
    setArtistName(value) {
        this.artistName = value;
        this.error = null;
    }

    @action.bound
    setAlbumName(value) {
        this.albumName = value;
        this.error = null;
    }

    @action.bound
    setDescription(value) {
        this.description = value;
        this.error = null;
    }

    @action.bound
    setFiles(selectedFiles) {
        this.files = Array.from(selectedFiles);
        this.error = null;
    }

    @action.bound
    removeFile(fileName) {
        this.files = this.files.filter((f) => f.name !== fileName);
    }

    @action.bound
    reset() {
        this.phase = "idle";
        this.artistName = "";
        this.albumName = "";
        this.description = "";
        this.files = [];
        this.fileProgresses = {};
        this.result = null;
        this.error = null;
        this.pendingSession = null;
        this.isFinalizing = false;
    }

    @action.bound
    restoreState({ artistName, albumName, description, files }) {
        this.phase = "idle";
        this.artistName = artistName || "";
        this.albumName = albumName || "";
        this.description = description || "";
        this.files = files || [];
        this.error = null;
    }

    @action.bound
    async initialize() {
        if (!this.producerId) return;
        const seq = ++this._initSeq;
        try {
            const session = await this.routeStore.getLatestUploadSession(this.producerId);
            if (this._initSeq !== seq) return;
            if (!session || !session.status) return;

            if (session.status === "Pending") {
                runInAction(() => {
                    if (this._initSeq !== seq) return;
                    this.pendingSession = {
                        sessionId: session.sessionId,
                        artistName: session.artistName,
                        albumName: session.albumName,
                        expectedFileNames: session.expectedFileNames || [],
                    };
                    this.phase = "pending";
                });
                return;
            }

            if (session.status === "Completed" || session.status === "Failed") {
                runInAction(() => {
                    if (this._initSeq !== seq) return;
                    this.result = {
                        accepted: session.accepted || [],
                        rejected: session.rejected || [],
                    };
                    this.phase = "result";
                });
                return;
            }

            // Expired or unknown — stay idle
        } catch (_) {
            // Network or API error — stay idle, don't block the user
        }
    }

    @action.bound
    async finalizeSession() {
        if (!this.pendingSession || !this.producerId || this.isFinalizing) return;
        this.isFinalizing = true;
        this.error = null;
        try {
            const expectedCount = this.pendingSession.expectedFileNames.length;
            const completeResult = await this.routeStore.completeUploadSession(
                this.producerId,
                this.pendingSession.sessionId
            );
            runInAction(() => {
                this._initSeq++;
                this.result = { ...completeResult, expectedCount };
                this.pendingSession = null;
                this.phase = "result";
                this.isFinalizing = false;
            });
        } catch (_) {
            // Something went wrong (session expired, network error, etc.) — send the user
            // back to the idle form so they can start a fresh upload.
            runInAction(() => {
                this.pendingSession = null;
                this.phase = "idle";
                this.isFinalizing = false;
            });
        }
    }

    @action.bound
    dismissPendingSession() {
        this.pendingSession = null;
        this.phase = "idle";
        this.error = null;
    }

    @action.bound
    async sendWithoutUpload() {
        if (!this.producerId) return;
        if (!this.artistName.trim() || !this.albumName.trim()) {
            runInAction(() => {
                this.error = "Artist name and album name are required.";
            });
            return;
        }
        if (this.artistName.trim().length > 200) {
            runInAction(() => {
                this.error = "Artist name must not exceed 200 characters.";
            });
            return;
        }
        if (this.albumName.trim().length > 200) {
            runInAction(() => {
                this.error = "Album name must not exceed 200 characters.";
            });
            return;
        }

        const ownProducerId = this.producerId;
        this.phase = "uploading";
        this.error = null;
        this.files = [];
        this.fileProgresses = {};

        try {
            const sessionResult = await this.routeStore.createUploadSession(this.producerId, {
                artistName: this.artistName.trim(),
                albumName: this.albumName.trim(),
                description: this.description.trim(),
                files: [],
            });

            const completeResult = await this.routeStore.completeUploadSession(
                this.producerId,
                sessionResult.sessionId
            );

            runInAction(() => {
                if (this.producerId !== ownProducerId) return;
                if (this.phase === "idle") return;
                this._initSeq++;
                this.result = {
                    accepted: completeResult.accepted || [],
                    rejected: completeResult.rejected || [],
                    expectedCount: 0,
                    isSendOnly: true,
                };
                this.phase = "result";
            });
        } catch (err) {
            runInAction(() => {
                if (this.producerId !== ownProducerId) return;
                this.error =
                    (err && err.data && err.data.message) ||
                    "An unexpected error occurred. Please try again.";
                this.phase = "idle";
            });
        }
    }

    @action.bound
    async startUpload() {
        if (!this.producerId) return;
        if (!this.files.length) {
            runInAction(() => {
                this.error = "Please add files to upload.";
            });
            return;
        }
        const ownProducerId = this.producerId;
        if (!this.artistName.trim() || !this.albumName.trim()) {
            runInAction(() => {
                this.error = "Artist name and album name are required.";
            });
            return;
        }
        if (this.artistName.trim().length > 200) {
            runInAction(() => {
                this.error = "Artist name must not exceed 200 characters.";
            });
            return;
        }
        if (this.albumName.trim().length > 200) {
            runInAction(() => {
                this.error = "Album name must not exceed 200 characters.";
            });
            return;
        }

        this.phase = "uploading";
        this.error = null;
        const initialProgresses = {};
        this.files.forEach((f) => {
            initialProgresses[f.name] = 0;
        });
        this.fileProgresses = initialProgresses;

        try {
            const fileData = this.files.map((f) => ({ fileName: f.name, size: f.size }));
            const sessionResult = await this.routeStore.createUploadSession(this.producerId, {
                artistName: this.artistName.trim(),
                albumName: this.albumName.trim(),
                description: this.description.trim(),
                files: fileData,
            });

            await Promise.all(
                (sessionResult.uploads || []).map((upload) => {
                    const file = this.files.find((f) => f.name === upload.fileName);
                    if (!file) return Promise.resolve();
                    return this._uploadToS3(file, upload.url, upload.contentType);
                })
            );

            const completeResult = await this.routeStore.completeUploadSession(
                this.producerId,
                sessionResult.sessionId
            );

            runInAction(() => {
                if (this.producerId !== ownProducerId) return;
                if (this.phase === "idle") return;
                this._initSeq++;
                this.result = {
                    ...completeResult,
                    expectedCount: this.pendingSession?.expectedFileNames?.length ?? this.files.length,
                };
                this.phase = "result";
            });
        } catch (err) {
            runInAction(() => {
                if (this.producerId !== ownProducerId) return;
                this.error =
                    (err && err.data && err.data.message) ||
                    "An unexpected error occurred. Please try again.";
                this.phase = "idle";
            });
        }
    }

    _uploadToS3(file, presignedUrl, contentType) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", presignedUrl);
            xhr.setRequestHeader("Content-Type", contentType || "audio/mpeg");

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    runInAction(() => {
                        this.fileProgresses = {
                            ...this.fileProgresses,
                            [file.name]: Math.round((event.loaded / event.total) * 100),
                        };
                    });
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    runInAction(() => {
                        this.fileProgresses = { ...this.fileProgresses, [file.name]: 100 };
                    });
                    resolve();
                } else {
                    reject(new Error(`Upload failed for ${file.name}: ${xhr.status}`));
                }
            };

            xhr.onerror = () => reject(new Error(`Network error uploading ${file.name}`));
            xhr.send(file);
        });
    }
}

export default ArtistUploadViewStore;
